import { api } from "~/api";
import {
  Character,
  OpenAIConfig,
  VoicevoxConfig,
  AivisSpeechConfig,
  VRMList,
} from "shared/Schemas";
import { Amplify } from "aws-amplify";
import { getCurrentUser } from "aws-amplify/auth";
import awsconfig from "~/aws-exports";
import { defaultPrompt } from "~/Commons";

// Amplify設定を初期化（既に初期化されていても安全）
Amplify.configure(awsconfig);

const ENV = process.env.REACT_APP_SYSTEM_REQUIREMENT || "onprem";

// Cognitoから顧客IDを取得する関数(リトライロジック付き)
// Cognitoセッション確立のリトライ設定
const COGNITO_SESSION_MAX_RETRIES = 3;
const COGNITO_SESSION_RETRY_DELAY_MS = 1000;

export const getCustomerId = async (): Promise<string> => {
  for (let attempt = 1; attempt <= COGNITO_SESSION_MAX_RETRIES; attempt++) {
    try {
      const currentUser = await getCurrentUser();
      return currentUser.userId;
    } catch (error) {
      if (attempt < COGNITO_SESSION_MAX_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, COGNITO_SESSION_RETRY_DELAY_MS * attempt)
        );
      } else {
        // 全てのリトライが失敗した場合
        console.error("Failed to get customer ID from Cognito:", error);
        throw new Error("Cognito authentication required. Please sign in to continue.");
      }
    }
  }

  // TypeScriptの型チェック用（実際にはここには到達しない）
  throw new Error("Unexpected error in getCustomerId");
};

// .envファイルからDynamoDBテーブル名を取得
const CHARACTER_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_CHARACTER_TABLE_NAME || "";
const VOICEVOX_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_VOICEVOX_TABLE_NAME || "";
const AIVISSPEECH_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_AIVISSPEECH_TABLE_NAME || "";
const OPENAI_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_OPENAI_TABLE_NAME || "";

// AWS環境の場合、DynamoDBテーブル名が全て設定されているか検証
if (
  ENV !== "onprem" &&
  (!CHARACTER_TABLE_NAME || !VOICEVOX_TABLE_NAME || !AIVISSPEECH_TABLE_NAME || !OPENAI_TABLE_NAME)
) {
  throw new Error(
    "DynamoDBテーブル名の環境変数が未設定です。以下の環境変数を全て設定してください: " +
      "REACT_APP_AWS_DYNAMODB_CHARACTER_TABLE_NAME, " +
      "REACT_APP_AWS_DYNAMODB_VOICEVOX_TABLE_NAME, " +
      "REACT_APP_AWS_DYNAMODB_AIVISSPEECH_TABLE_NAME, " +
      "REACT_APP_AWS_DYNAMODB_OPENAI_TABLE_NAME"
  );
}
// DynamoDB用のキャラクターデータ型（実際の構造に基づく）
interface DynamoDBCharacter {
  customerId: string;
  characterId: string;
  name: string;
  globalName: string;
  ttsEngine: string;
  characterPrompt: string;
  modelPath?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  deletedAt?: string;
}

// SQLiteでキャラクター関連データを作成するヘルパー関数
const createOnPremCharacterConfigs = async (
  voicevox_speaker_id: number,
  voicevox_speed: number,
  voicevox_pitch: number,
  voicevox_intonation: number,
  openai_speaker_id: number,
  aivis_speech_speaker_id: number,
  aivis_speech_speed: number,
  aivis_speech_pitch: number,
  aivis_speech_intonation: number
) => {
  // 1. voicevox_configテーブルに挿入してIDを取得
  const voicevoxConfigResponse = (await api.dbWithParams.query({
    sql: `INSERT INTO voicevox_config (speaker_id, speed, pitch, intonation)
          VALUES (?, ?, ?, ?) RETURNING id;`,
    params: [voicevox_speaker_id, voicevox_speed, voicevox_pitch, voicevox_intonation],
  })) as VoicevoxConfig[];

  const voicevoxConfigId = voicevoxConfigResponse[0]?.id;
  if (!voicevoxConfigId) {
    throw new Error("Failed to insert into voicevox_config or retrieve ID.");
  }

  // 2. openai_configテーブルに挿入してIDを取得
  const openaiConfigResponse = (await api.dbWithParams.query({
    sql: "INSERT INTO openai_config (speaker_id) VALUES (?) RETURNING id;",
    params: [openai_speaker_id],
  })) as OpenAIConfig[];

  const openaiConfigId = openaiConfigResponse[0]?.id;
  if (!openaiConfigId) {
    throw new Error("Failed to insert into openai_config or retrieve ID.");
  }

  // 3. aivis_speech_configテーブルに挿入してIDを取得
  const aivisSpeechConfigResponse = (await api.dbWithParams.query({
    sql: `INSERT INTO aivis_speech_config (speaker_id, speed, pitch, intonation)
          VALUES (?, ?, ?, ?) RETURNING id;`,
    params: [
      aivis_speech_speaker_id,
      aivis_speech_speed,
      aivis_speech_pitch,
      aivis_speech_intonation,
    ],
  })) as AivisSpeechConfig[];

  const aivisSpeechConfigId = aivisSpeechConfigResponse[0]?.id;
  if (!aivisSpeechConfigId) {
    throw new Error("Failed to insert into aivis_speech_config or retrieve ID.");
  }

  return { voicevoxConfigId, openaiConfigId, aivisSpeechConfigId };
};

// VRMファイルパスを取得するヘルパー関数
const getVRMModelPath = async (global_name: string): Promise<string> => {
  try {
    // VRMファイル一覧を取得してglobal_nameに対応するパスを見つける
    const customerId = await getCustomerId();
    const vrmFiles = (await api.animation.getVRMfilesAWS.query({
      customerId: customerId,
    })) as VRMList[];

    // global_name + ".vrm" に一致するVRMファイルを探す
    const targetVrmFile = vrmFiles.find((vrm) => vrm.name === `${global_name}.vrm`);
    if (targetVrmFile) {
      return targetVrmFile.path;
    } else {
      console.error(`VRM file not found for global_name: ${global_name}`);
      return "";
    }
  } catch (error) {
    console.error("Error fetching VRM files for modelPath:", error);
    return "";
  }
};

// AWS DynamoDBでキャラクター関連データを作成するヘルパー関数
const createAWSCharacterConfigs = async (
  characterId: string,
  currentTime: string,
  voicevox_speaker_id: number,
  voicevox_speed: number,
  voicevox_pitch: number,
  voicevox_intonation: number,
  openai_speaker_id: number,
  aivis_speech_speaker_id: number,
  aivis_speech_speed: number,
  aivis_speech_pitch: number,
  aivis_speech_intonation: number
) => {
  const customerId = await getCustomerId();

  // 2. voicevox-configsテーブルに挿入
  const voicevoxConfig = {
    customerId: customerId,
    characterId: characterId,
    speakerId: voicevox_speaker_id,
    speed: voicevox_speed,
    pitch: voicevox_pitch,
    intonation: voicevox_intonation,
    createdAt: currentTime,
    updatedAt: currentTime,
    createdBy: "system",
    updatedBy: "system",
  };

  const voicevoxResult = await api.dynamodb.putItem.mutate({
    tableName: VOICEVOX_TABLE_NAME,
    itemData: voicevoxConfig,
    conditionExpression: "attribute_not_exists(characterId)",
  });

  // 既存データがある場合はログ出力のみで処理続行
  if ("alreadyExists" in voicevoxResult && voicevoxResult.alreadyExists) {
    console.info("Voicevox config already exists for character:", characterId);
  }

  // 3. openai-configsテーブルに挿入
  const openaiConfig = {
    customerId: customerId,
    characterId: characterId,
    speakerId: openai_speaker_id,
    createdAt: currentTime,
    updatedAt: currentTime,
    createdBy: "system",
    updatedBy: "system",
  };

  const openaiResult = await api.dynamodb.putItem.mutate({
    tableName: OPENAI_TABLE_NAME,
    itemData: openaiConfig,
    conditionExpression: "attribute_not_exists(characterId)",
  });

  // 既存データがある場合はログ出力のみで処理続行
  if ("alreadyExists" in openaiResult && openaiResult.alreadyExists) {
    console.info("OpenAI config already exists for character:", characterId);
  }

  // 4. aivisspeech-configsテーブルに挿入
  const aivisSpeechConfig = {
    customerId: customerId,
    characterId: characterId,
    speakerId: aivis_speech_speaker_id,
    speed: aivis_speech_speed,
    pitch: aivis_speech_pitch,
    intonation: aivis_speech_intonation,
    createdAt: currentTime,
    updatedAt: currentTime,
    createdBy: "system",
    updatedBy: "system",
  };

  const aivisSpeechResult = await api.dynamodb.putItem.mutate({
    tableName: AIVISSPEECH_TABLE_NAME,
    itemData: aivisSpeechConfig,
    conditionExpression: "attribute_not_exists(characterId)",
  });

  // 既存データがある場合はログ出力のみで処理続行
  if ("alreadyExists" in aivisSpeechResult && aivisSpeechResult.alreadyExists) {
    console.info("AivisSpeech config already exists for character:", characterId);
  }
};

// SQLiteでキャラクター関連設定を更新するヘルパー関数
const updateOnPremCharacterConfigs = async (
  currentCharacter: Character,
  voicevox_speaker_id: number,
  voicevox_speed: number,
  voicevox_pitch: number,
  voicevox_intonation: number,
  openai_speaker_id: number,
  aivis_speech_speaker_id: number,
  aivis_speech_speed: number,
  aivis_speech_pitch: number,
  aivis_speech_intonation: number
) => {
  // 1. voicevox_configを更新
  const voicevoxConfigId = currentCharacter.voicevox_config;
  await api.dbWithParams.query({
    sql: `UPDATE voicevox_config
          SET speaker_id = ?,
              speed      = ?,
              pitch      = ?,
              intonation = ?
          WHERE id = ?;`,
    params: [
      voicevox_speaker_id,
      voicevox_speed,
      voicevox_pitch,
      voicevox_intonation,
      voicevoxConfigId,
    ],
  });

  // 2. openai_configを更新
  const openaiConfigId = currentCharacter.openai_config;
  await api.dbWithParams.query({
    sql: "UPDATE openai_config SET speaker_id = ? WHERE id = ?;",
    params: [openai_speaker_id, openaiConfigId],
  });

  // 3. aivis_speech_configを更新
  const aivisSpeechConfigId = currentCharacter.aivis_speech_config;
  await api.dbWithParams.query({
    sql: `UPDATE aivis_speech_config
          SET speaker_id = ?,
              speed      = ?,
              pitch      = ?,
              intonation = ?
          WHERE id = ?;`,
    params: [
      aivis_speech_speaker_id,
      aivis_speech_speed,
      aivis_speech_pitch,
      aivis_speech_intonation,
      aivisSpeechConfigId,
    ],
  });
};

// AWSでTTSエンジン別の設定を更新するヘルパー関数
const updateAWSTTSConfigs = async (
  tts_engine: string,
  characterId: string,
  currentTime: string,
  voicevox_speaker_id: number,
  voicevox_speed: number,
  voicevox_pitch: number,
  voicevox_intonation: number,
  openai_speaker_id: number,
  aivis_speech_speaker_id: number,
  aivis_speech_speed: number,
  aivis_speech_pitch: number,
  aivis_speech_intonation: number
) => {
  const customerId = await getCustomerId();

  // 2. ttsEngineの種類に応じて対応する音声設定テーブルを更新
  if (tts_engine === "VOICEVOX") {
    const voicevoxUpdateData = {
      speakerId: voicevox_speaker_id,
      speed: voicevox_speed,
      pitch: voicevox_pitch,
      intonation: voicevox_intonation,
      updatedAt: currentTime,
      updatedBy: "system",
    };

    await api.dynamodb.updateItem.mutate({
      tableName: VOICEVOX_TABLE_NAME,
      key: {
        customerId: customerId,
        characterId: characterId,
      },
      updateData: voicevoxUpdateData,
    });
  } else if (tts_engine === "AivisSpeech") {
    const aivisSpeechUpdateData = {
      speakerId: aivis_speech_speaker_id,
      speed: aivis_speech_speed,
      pitch: aivis_speech_pitch,
      intonation: aivis_speech_intonation,
      updatedAt: currentTime,
      updatedBy: "system",
    };

    await api.dynamodb.updateItem.mutate({
      tableName: AIVISSPEECH_TABLE_NAME,
      key: {
        customerId: customerId,
        characterId: characterId,
      },
      updateData: aivisSpeechUpdateData,
    });
  } else if (tts_engine === "OpenAI") {
    const openaiUpdateData = {
      speakerId: openai_speaker_id,
      updatedAt: currentTime,
      updatedBy: "system",
    };

    await api.dynamodb.updateItem.mutate({
      tableName: OPENAI_TABLE_NAME,
      key: {
        customerId: customerId,
        characterId: characterId,
      },
      updateData: openaiUpdateData,
    });
  }
};

/**
 * characterテーブルからすべてのデータを取得する
 * @returns キャラクター情報の配列
 */
export const selectAll = async (): Promise<Character[]> => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、sqliteの処理
      const response = await api.db.query(`
          SELECT *
          FROM character;
      `);
      return response as Character[];
    } else {
      // AWS環境の場合、DynamoDBの処理
      const customerId = await getCustomerId();
      const response = await api.dynamodb.queryItems.query({
        tableName: CHARACTER_TABLE_NAME,
        filterKey: "customerId",
        filterValue: customerId,
      });

      if (response.success && "items" in response && response.items) {
        // DynamoDBの構造をCharacter型に変換
        const characters = (response.items as DynamoDBCharacter[]).map((item) => ({
          name: item.name,
          global_name: item.globalName,
          tts_engine: item.ttsEngine,
          prompt: item.characterPrompt || defaultPrompt,
          voicevox_config: 1, // 仮の値
          openai_config: 1, // 仮の値
          aivis_speech_config: 1, // 仮の値
        }));
        return characters as Character[];
      }

      console.error("Failed to fetch characters from DynamoDB:", response);
      return [];
    }
  } catch (error) {
    console.error("Error in selectAll:", error);
    return [];
  }
};

/**
 * 利用可能なVRMに対応するキャラクターのみ取得する
 * (オンプレ版：server/public/models, AWS版：S3に存在するVRMファイル が利用可能になる)
 * @param vrmList 利用可能なVRM一覧
 * @returns 利用可能なキャラクター情報の配列
 */
export const selectAvailableCharacters = async (
  vrmList: Pick<VRMList, "name">[]
): Promise<Character[]> => {
  const characters = await selectAll();
  // ロードしたVRMファイル名から拡張子を除いた名前を抽出する
  const availableGlobalNames = new Set(
    vrmList.filter((vrm) => /\.vrm$/i.test(vrm.name)).map((vrm) => vrm.name.replace(/\.vrm$/i, ""))
  );

  return (
    characters
      .filter(
        (character) =>
          character.global_name !== "" && availableGlobalNames.has(character.global_name)
      )
      // global_nameの辞書順で並びを安定化する
      .sort((left, right) => left.global_name.localeCompare(right.global_name, "ja"))
  );
};

/**
 * characterテーブルから指定した名前のデータを取得する
 * @param name キャラクター名
 * @returns キャラクター情報またはnull
 */
// eslint-disable-next-line complexity
export const selectByName = async (name: string): Promise<Character | null> => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理
      const response = (await api.dbWithParams.query({
        sql: "SELECT * FROM character WHERE name = ?;",
        params: [name],
      })) as Character[];

      if (response.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(`No character found with name: ${name}`);
        return null;
      }
      // promptがnullまたは空文字の場合、defaultPromptを設定
      if (!response[0].prompt) {
        response[0].prompt = defaultPrompt;
      }
      return response[0] as Character;
    } else {
      // AWS環境の場合、DynamoDBの処理
      // まずcustomerIdでフィルタリングしてから、名前で探す
      const customerId = await getCustomerId();
      const response = await api.dynamodb.queryItems.query({
        tableName: CHARACTER_TABLE_NAME,
        filterKey: "customerId",
        filterValue: customerId,
      });

      if (response.success && "items" in response && response.items && response.items.length > 0) {
        // customerIdでフィルタリングされたアイテムから名前で検索
        const foundItem = (response.items as DynamoDBCharacter[]).find(
          (item) => item.name === name
        );
        if (foundItem) {
          const character: Character = {
            name: foundItem.name,
            global_name: foundItem.globalName,
            tts_engine: foundItem.ttsEngine,
            prompt: foundItem.characterPrompt || defaultPrompt,
            voicevox_config: 1, // 仮の値
            openai_config: 1, // 仮の値
            aivis_speech_config: 1, // 仮の値
          };
          return character;
        }
      }

      // eslint-disable-next-line no-console
      console.warn(`No character found with name: ${name}`);
      return null;
    }
  } catch (error) {
    console.error("Error in selectByName:", error);
    return null;
  }
};

/**
 * キャラクターのVoicevox設定を取得する
 * @param name キャラクター名
 * @returns Voicevox設定情報
 */
// DynamoDBから特定のキャラクターのcharacterIdを取得するヘルパー関数
// const getCharacterIdByName = async (name: string): Promise<string | null> => {
//   try {
//     const characterIdResponse = await api.dynamodb.queryItems.query({
//       tableName: CHARACTER_TABLE_NAME,
//       filterKey: "customerId",
//       filterValue: CUSTOMER_ID
//     });
//
//     if (characterIdResponse.success && "items" in characterIdResponse && characterIdResponse.items) {
//       const foundChar = (characterIdResponse.items as DynamoDBCharacter[]).find(
//         (item) => item.name === name
//       );
//       return foundChar ? foundChar.characterId : null;
//     }
//     return null;
//   } catch (error) {
//     console.error("Error getting characterId:", error);
//     return null;
//   }
// };

// DynamoDBからVoicevox設定を取得するヘルパー関数
const getVoicevoxConfigFromDynamoDB = async (
  characterId: string
): Promise<VoicevoxConfig | null> => {
  try {
    const customerId = await getCustomerId();
    const configResponse = await api.dynamodb.getItem.query({
      tableName: VOICEVOX_TABLE_NAME,
      key: {
        customerId: customerId,
        characterId: characterId,
      },
    });

    if (configResponse.success && "item" in configResponse && configResponse.item) {
      const config = configResponse.item as {
        speakerId: string;
        speed: string;
        pitch: string;
        intonation: string;
      };
      return {
        id: 0, // DynamoDBでは直接IDは使わない
        speaker_id: parseInt(config.speakerId) || 0,
        speed: parseFloat(config.speed) || 1.0,
        pitch: parseFloat(config.pitch) || 0.0,
        intonation: parseFloat(config.intonation) || 1.0,
      };
    }
    return null;
  } catch (error) {
    console.error("Error getting voicevox config from DynamoDB:", error);
    return null;
  }
};

/**
 * キャラクターのVoicevox設定を取得する
 * @param name キャラクター名
 * @returns Voicevox設定情報
 */
export const getVoicevoxConfig = async (name: string): Promise<VoicevoxConfig | null> => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理（JOIN）
      const response = (await api.dbWithParams.query({
        sql: `SELECT vc.id, vc.speaker_id, vc.speed, vc.pitch, vc.intonation
              FROM character c
                       INNER JOIN voicevox_config vc ON c.voicevox_config = vc.id
              WHERE c.name = ?`,
        params: [name],
      })) as VoicevoxConfig[];

      if (response.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(`No voicevox_config found for character with name: ${name}`);
        return null;
      }

      // 取得したデータを明示的に指定して返す
      const voicevoxConfig = response[0];
      return {
        id: voicevoxConfig.id,
        speaker_id: voicevoxConfig.speaker_id,
        speed: voicevoxConfig.speed,
        pitch: voicevoxConfig.pitch,
        intonation: voicevoxConfig.intonation,
      };
    } else {
      // AWS環境の場合、characterからcharacterIdを取得してVOICEVOX_TABLE_NAMEから設定を取得
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      if (!characterId) {
        // eslint-disable-next-line no-console
        console.warn(`No characterId found for character with name: ${name}`);
        return null;
      }

      return await getVoicevoxConfigFromDynamoDB(characterId);
    }
  } catch (error) {
    console.error("Error in getVoicevoxConfig:", error);
    return null;
  }
};

/**
 * キャラクターのOpenAI設定を取得する
 * @param name キャラクター名
 * @returns OpenAI設定情報
 */
export const getOpenaiConfig = async (name: string): Promise<OpenAIConfig | null> => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理（JOIN）
      const response = (await api.dbWithParams.query({
        sql: `SELECT oc.id, oc.speaker_id
              FROM character c
                       INNER JOIN openai_config oc ON c.openai_config = oc.id
              WHERE c.name = ?`,
        params: [name],
      })) as OpenAIConfig[];

      if (response.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(`No openai_config found for character with name: ${name}`);
        return null;
      }

      // 取得したデータを明示的に指定して返す
      const openaiConfig = response[0];
      return {
        id: openaiConfig.id,
        speaker_id: openaiConfig.speaker_id,
      };
    } else {
      // AWS環境の場合、DynamoDBの処理（別テーブル）
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      if (!characterId) {
        // eslint-disable-next-line no-console
        console.warn(`No characterId found for character with name: ${name}`);
        return null;
      }

      const configResponse = await api.dynamodb.getItem.query({
        tableName: OPENAI_TABLE_NAME,
        key: {
          customerId: customerId,
          characterId: characterId,
        },
      });

      if (configResponse.success && "item" in configResponse && configResponse.item) {
        const config = configResponse.item as {
          speakerId: string;
        };
        return {
          id: 0,
          speaker_id: parseInt(config.speakerId) || 0,
        };
      }

      // eslint-disable-next-line no-console
      console.warn(`No openai_config found for character with name: ${name}`);
      return null;
    }
  } catch (error) {
    console.error("Error in getOpenaiConfig:", error);
    return null;
  }
};

// DynamoDBからAivisSpeech設定を取得するヘルパー関数
const getAivisSpeechConfigFromDynamoDB = async (
  characterId: string
): Promise<AivisSpeechConfig | null> => {
  try {
    const customerId = await getCustomerId();
    const configResponse = await api.dynamodb.getItem.query({
      tableName: AIVISSPEECH_TABLE_NAME,
      key: {
        customerId: customerId,
        characterId: characterId,
      },
    });

    if (configResponse.success && "item" in configResponse && configResponse.item) {
      const config = configResponse.item as {
        speakerId: string;
        speed: string;
        pitch: string;
        intonation: string;
      };
      return {
        id: 0,
        speaker_id: parseInt(config.speakerId) || 0,
        speed: parseFloat(config.speed) || 1.0,
        pitch: parseFloat(config.pitch) || 0.0,
        intonation: parseFloat(config.intonation) || 1.0,
      };
    }
    return null;
  } catch (error) {
    console.error("Error getting aivis speech config from DynamoDB:", error);
    return null;
  }
};

/**
 * キャラクターのAIVIS Speech設定を取得する
 * @param name キャラクター名
 * @returns AIVIS Speech設定情報
 */
export const getAivisSpeechConfig = async (name: string): Promise<AivisSpeechConfig | null> => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理（JOIN）
      const response = (await api.dbWithParams.query({
        sql: `SELECT asc.id, asc.speaker_id, asc.speed, asc.pitch, asc.intonation
              FROM character c
              INNER JOIN aivis_speech_config asc
              ON c.aivis_speech_config = asc.id
              WHERE c.name = ?`,
        params: [name],
      })) as AivisSpeechConfig[];

      if (response.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(`No aivis_speech_config found for character with name: ${name}`);
        return null;
      }

      // 取得したデータを明示的に指定して返す
      const aivisSpeechConfig = response[0];
      return {
        id: aivisSpeechConfig.id,
        speaker_id: aivisSpeechConfig.speaker_id,
        speed: aivisSpeechConfig.speed,
        pitch: aivisSpeechConfig.pitch,
        intonation: aivisSpeechConfig.intonation,
      };
    } else {
      // AWS環境の場合、DynamoDBの処理（別テーブル）
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      if (!characterId) {
        // eslint-disable-next-line no-console
        console.warn(`No characterId found for character with name: ${name}`);
        return null;
      }

      return await getAivisSpeechConfigFromDynamoDB(characterId);
    }
  } catch (error) {
    console.error("Error in getAivisSpeechConfig:", error);
    return null;
  }
};

/**
 * characterテーブルに新規データを追加する
 * @param name キャラクター名
 * @param global_name グローバル名
 * @param tts_engine TTS エンジン
 * @param prompt プロンプト
 * @param voicevox_speaker_id Voicevox話者ID
 * @param voicevox_speed Voicevox速度
 * @param voicevox_pitch Voicevoxピッチ
 * @param voicevox_intonation Voicevoxイントネーション
 * @param openai_speaker_id OpenAI話者ID
 * @param aivis_speech_speaker_id AIVIS Speech話者ID
 * @param aivis_speech_speed AIVIS Speech速度
 * @param aivis_speech_pitch AIVIS Speechピッチ
 * @param aivis_speech_intonation AIVIS Speechイントネーション
 * @returns 追加結果
 */
export const insert = async (
  name: string,
  global_name: string,
  tts_engine: string,
  prompt: string,
  voicevox_speaker_id: number,
  voicevox_speed: number,
  voicevox_pitch: number,
  voicevox_intonation: number,
  openai_speaker_id: number,
  aivis_speech_speaker_id: number,
  aivis_speech_speed: number,
  aivis_speech_pitch: number,
  aivis_speech_intonation: number
) => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理（正規化されたテーブル構造）
      const { voicevoxConfigId, openaiConfigId, aivisSpeechConfigId } =
        await createOnPremCharacterConfigs(
          voicevox_speaker_id,
          voicevox_speed,
          voicevox_pitch,
          voicevox_intonation,
          openai_speaker_id,
          aivis_speech_speaker_id,
          aivis_speech_speed,
          aivis_speech_pitch,
          aivis_speech_intonation
        );

      // 4. characterテーブルに挿入（aivis_speech_configを含む）
      const characterResponse = await api.dbWithParams.query({
        sql: `INSERT INTO character (name, global_name, tts_engine, prompt, voicevox_config, openai_config,
                                     aivis_speech_config)
              VALUES (?, ?, ?, ?, ?, ?, ?)
              ON CONFLICT(name) DO UPDATE SET
                global_name = excluded.global_name,
                tts_engine = excluded.tts_engine,
                prompt = excluded.prompt,
                voicevox_config = excluded.voicevox_config,
                openai_config = excluded.openai_config,
                aivis_speech_config = excluded.aivis_speech_config;`,
        params: [
          name,
          global_name,
          tts_engine,
          prompt,
          voicevoxConfigId,
          openaiConfigId,
          aivisSpeechConfigId,
        ],
      });

      return characterResponse;
    } else {
      // AWS環境の場合、DynamoDBの処理（複数の別テーブルに挿入）
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      const currentTime = new Date().toISOString();

      // 1. charactersテーブルに挿入
      // AWSの場合、S3のVRMファイルパス（キー）を取得
      const modelPath = await getVRMModelPath(global_name);

      const characterData = {
        customerId: customerId,
        characterId: characterId,
        name: name,
        globalName: global_name,
        ttsEngine: tts_engine,
        characterPrompt: prompt,
        modelPath: modelPath,
        createdAt: currentTime,
        updatedAt: currentTime,
        createdBy: "system",
        updatedBy: "system",
        deletedAt: "",
      };

      const putItemResult = await api.dynamodb.putItem.mutate({
        tableName: CHARACTER_TABLE_NAME,
        itemData: characterData,
        conditionExpression: "attribute_not_exists(characterId)",
      });

      // 既存データがある場合はスキップして成功として扱う
      if ("alreadyExists" in putItemResult && putItemResult.alreadyExists) {
        return {
          success: true,
          characterId,
          message: "キャラクターは既に存在します",
          alreadyExists: true,
        };
      }

      // 2-4. 関連する設定テーブルに挿入
      await createAWSCharacterConfigs(
        characterId,
        currentTime,
        voicevox_speaker_id,
        voicevox_speed,
        voicevox_pitch,
        voicevox_intonation,
        openai_speaker_id,
        aivis_speech_speaker_id,
        aivis_speech_speed,
        aivis_speech_pitch,
        aivis_speech_intonation
      );

      return { success: true, characterId };
    }
  } catch (error) {
    console.error("Error inserting character:", error);
    throw error;
  }
};

/**
 * characterテーブルの指定した名前のデータを削除する
 * @param name キャラクター名
 * @returns 削除結果
 */
export const deleteByName = async (name: string) => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理
      const response = await api.dbWithParams.query({
        sql: "DELETE FROM character WHERE name = ?;",
        params: [name],
      });
      return response;
    } else {
      // AWS環境の場合、DynamoDBの処理
      // 名前からcharacterIdを取得
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      if (!characterId) {
        throw new Error(`Character with name '${name}' not found in DynamoDB.`);
      }

      const response = await api.dynamodb.deleteItem.mutate({
        tableName: CHARACTER_TABLE_NAME,
        key: {
          characterId: characterId,
        },
      });
      return response;
    }
  } catch (error) {
    console.error(`Error deleting character with name ${name}:`, error);
    throw error;
  }
};

/**
 * 特定のカラムの値を編集する
 * @param name キャラクター名
 * @param column 編集するカラム名
 * @param value 新しい値
 * @returns 更新結果
 */
export const update = async (name: string, column: string, value: string) => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理
      // 明示的なカラムマッピング（識別子の直接埋め込みを防ぐため）
      const validColumns: Record<string, string> = {
        name: "name",
        global_name: "global_name",
        tts_engine: "tts_engine",
        prompt: "prompt",
      };

      const safeColumn = validColumns[column];
      if (!safeColumn) {
        throw new Error(`Invalid column name: ${column}`);
      }

      const response = await api.dbWithParams.query({
        sql: `UPDATE character
              SET ${safeColumn} = ?
              WHERE name = ?;`,
        params: [value, name],
      });
      return response;
    } else {
      // AWS環境の場合、DynamoDBの処理
      // 有効なカラムのチェック
      const validColumns = ["name", "globalName", "ttsEngine", "characterPrompt"];
      if (!validColumns.includes(column)) {
        throw new Error(`Invalid column name: ${column}`);
      }

      // 名前からcharacterIdを取得
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      if (!characterId) {
        throw new Error(`Character with name '${name}' not found in DynamoDB.`);
      }

      const updateData: Record<string, string> = {};
      updateData[column] = value;

      const response = await api.dynamodb.updateItem.mutate({
        tableName: CHARACTER_TABLE_NAME,
        key: {
          characterId: characterId,
        },
        updateData,
        conditionExpression: "customerId = :customerId",
        conditionExpressionValues: { ":customerId": customerId },
      });
      return response;
    }
  } catch (error) {
    console.error(`Error updating character column ${column}:`, error);
    throw error;
  }
};

/**
 * characterテーブルの指定した名前のデータを編集する
 * @param name キャラクター名
 * @param global_name グローバル名
 * @param tts_engine TTS エンジン
 * @param prompt プロンプト
 * @param voicevox_speaker_id Voicevox話者ID
 * @param voicevox_speed Voicevox速度
 * @param voicevox_pitch Voicevoxピッチ
 * @param voicevox_intonation Voicevoxイントネーション
 * @param openai_speaker_id OpenAI話者ID
 * @param aivis_speech_speaker_id AIVIS Speech話者ID
 * @param aivis_speech_speed AIVIS Speech速度
 * @param aivis_speech_pitch AIVIS Speechピッチ
 * @param aivis_speech_intonation AIVIS Speechイントネーション
 * @returns 更新結果
 */
export const updateByName = async (
  name: string,
  global_name: string,
  tts_engine: string,
  prompt: string,
  voicevox_speaker_id: number,
  voicevox_speed: number,
  voicevox_pitch: number,
  voicevox_intonation: number,
  openai_speaker_id: number,
  aivis_speech_speaker_id: number,
  aivis_speech_speed: number,
  aivis_speech_pitch: number,
  aivis_speech_intonation: number
) => {
  try {
    // もしglobal_name=""かつDBのglobal_nameが存在している場合は、DBのglobal_nameをそのまま使う
    let finalGlobalName = global_name;
    if (global_name === "") {
      const selectedCharacter = await selectByName(name);
      if (selectedCharacter && selectedCharacter.global_name !== "") {
        finalGlobalName = selectedCharacter.global_name;
      }
    }

    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理（複数テーブル更新）
      // 現在のcharacterデータを取得
      const currentCharacter = await selectByName(name);
      if (!currentCharacter) {
        throw new Error(`Character with name '${name}' not found.`);
      }

      // 関連する設定テーブルを更新
      await updateOnPremCharacterConfigs(
        currentCharacter,
        voicevox_speaker_id,
        voicevox_speed,
        voicevox_pitch,
        voicevox_intonation,
        openai_speaker_id,
        aivis_speech_speaker_id,
        aivis_speech_speed,
        aivis_speech_pitch,
        aivis_speech_intonation
      );

      // 4. characterテーブルを更新
      const response = await api.dbWithParams.query({
        sql: `UPDATE character
              SET global_name = ?,
                  tts_engine  = ?,
                  prompt      = ?
              WHERE name = ?;`,
        params: [finalGlobalName, tts_engine, prompt, name],
      });

      return response;
    } else {
      // AWS環境の場合、DynamoDBの処理（複数テーブル更新）
      const customerId = await getCustomerId();
      const characterId = `${customerId}#${name}`;
      const currentTime = new Date().toISOString();

      // 1. charactersテーブルを更新
      const characterUpdateData = {
        globalName: finalGlobalName,
        ttsEngine: tts_engine,
        characterPrompt: prompt,
        updatedAt: currentTime,
        updatedBy: "system",
      };

      await api.dynamodb.updateItem.mutate({
        tableName: CHARACTER_TABLE_NAME,
        key: {
          characterId: characterId,
        },
        updateData: characterUpdateData,
        conditionExpression: "customerId = :customerId",
        conditionExpressionValues: { ":customerId": customerId },
      });

      // 2. TTS設定テーブルを更新
      await updateAWSTTSConfigs(
        tts_engine,
        characterId,
        currentTime,
        voicevox_speaker_id,
        voicevox_speed,
        voicevox_pitch,
        voicevox_intonation,
        openai_speaker_id,
        aivis_speech_speaker_id,
        aivis_speech_speed,
        aivis_speech_pitch,
        aivis_speech_intonation
      );

      return { success: true, characterId };
    }
  } catch (error) {
    console.error("Error updating character:", error);
    throw error;
  }
};

/**
 * characterテーブルのデータを全削除する
 * @returns 削除結果
 */
export const deleteAll = async () => {
  try {
    if (ENV === "onprem") {
      // オンプレ環境の場合、SQLiteの処理
      const response = await api.db.query(`
          DELETE
          FROM character;
      `);
      return response;
    } else {
      // AWS環境の場合、DynamoDBの処理
      // DynamoDBでは全削除のためにまず全アイテムを取得してから個別削除
      const allCharacters = await selectAll();
      const customerId = await getCustomerId();

      const deletePromises = allCharacters.map((character) =>
        api.dynamodb.deleteItem.mutate({
          tableName: CHARACTER_TABLE_NAME,
          key: {
            characterId: `${customerId}#${character.name}`,
          },
        })
      );

      const results = await Promise.all(deletePromises);
      return {
        success: true,
        message: `Deleted ${results.length} characters`,
        results,
      };
    }
  } catch (error) {
    console.error("Error deleting all characters:", error);
    throw error;
  }
};
